import { POWER_PERIODS } from '@/constants/supplyRates'
import { normalizeContractCups, isValidContractCups } from '@/services/contractCups'
import { lightRateOptions, gasRateOptions } from '@/services/estateSupplyRates'

/**
 * Suministros de la ficha del inmueble (ADS-0124).
 *
 * Las tablas de luz y gas muestran CUPS, dirección, código postal, municipio,
 * provincia y tarifa; la de luz añade las potencias P1-P6 en una sola celda.
 * Municipio y provincia viven en el inmueble; dirección y CP pueden ser del
 * propio suministro o heredarse del inmueble. El formulario de la ficha añade
 * o edita un suministro con CUPS, tarifa del catálogo y, solo en luz, potencias.
 */
const value = (...candidates) => {
  for (const candidate of candidates) {
    if (candidate !== null && candidate !== undefined && String(candidate).trim() !== '') return candidate
  }
  return '-'
}

const formatPower = (power) => {
  if (power === null || power === undefined || String(power).trim() === '') return '–'
  const number = Number(power)
  return Number.isFinite(number) ? String(number).replace('.', ',') : String(power)
}

/** "4,6 / 5,75 / – / – / – / –"; vacío si ninguna potencia está informada. */
export const formatPowers = (supply) => {
  const powers = POWER_PERIODS.map((period) => supply?.[period])
  if (powers.every((power) => power === null || power === undefined || String(power).trim() === '')) return '-'
  return powers.map(formatPower).join(' / ')
}

export const estateSupplyRow = (supply, property = {}) => ({
  ...supply,
  cups: value(supply.cups),
  address: value(supply.address, property?.address),
  postal_code: value(supply.postal_code, property?.postal_code),
  municipality: value(supply.municipality, property?.municipality),
  province: value(supply.province, property?.province),
  rate: value(supply.rate),
  powers: formatPowers(supply),
})

export const estateSupplyRows = (supplies, property = {}) =>
  Array.isArray(supplies) ? supplies.map((supply) => estateSupplyRow(supply, property)) : []

/** Columnas de la tabla de la ficha; la de gas no lleva potencias. */
export const estateSupplyColumns = (type, t) => [
  { key: 'cups', label: t('property.estateSupplies.columns.cups') },
  { key: 'address', label: t('property.estateSupplies.columns.address') },
  { key: 'postal_code', label: t('property.estateSupplies.columns.postalCode') },
  { key: 'municipality', label: t('property.estateSupplies.columns.municipality') },
  { key: 'province', label: t('property.estateSupplies.columns.province') },
  { key: 'rate', label: t('property.estateSupplies.columns.rate') },
  ...(type === 'light' ? [{ key: 'powers', label: t('property.estateSupplies.columns.powers') }] : []),
  { key: 'opciones', label: t('property.estateSupplies.columns.options') },
]

// --- Formulario de la ficha -----------------------------------------------

export const emptyEstateSupplyForm = (type) => {
  // Cada CUPS nuevo necesita una dirección y un código postal expresos,
  // aunque la ficha del titular ya tenga ambos datos.
  return {
    type,
    cups: '',
    address: '',
    postal_code: '',
    rate: '',
    ...Object.fromEntries(POWER_PERIODS.map((period) => [period, ''])),
  }
}

export const estateSupplyForm = (supply, property = {}, type = 'light') => {
  const form = emptyEstateSupplyForm(supply?.type || type)
  if (!supply) return form
  form.cups = supply.cups ?? ''
  form.address = supply.address ?? property?.address ?? ''
  form.postal_code = supply.postal_code ?? property?.postal_code ?? ''
  form.rate = supply.rate ?? ''
  for (const period of POWER_PERIODS) {
    const power = supply[period]
    form[period] = power === null || power === undefined ? '' : String(Number(power))
  }
  return form
}

export const estateSupplyRateOptions = (type, current = '') =>
  type === 'gas' ? gasRateOptions(current) : lightRateOptions(current)

const toNumber = (raw) => {
  const text = String(raw ?? '').trim().replace(',', '.')
  if (text === '') return null
  const number = Number(text)
  return Number.isFinite(number) ? number : NaN
}

/**
 * Errores como claves de traducción por campo. Al añadir, el CUPS es obligatorio;
 * al editar un suministro que nunca lo tuvo (alta de ADS-0112) puede seguir vacío.
 */
export const validateEstateSupply = (form, { requireCups = true, requireAddress = false, requirePostalCode = false } = {}) => {
  const errors = {}
  const cups = normalizeContractCups(form.cups)
  if ((requireCups || cups !== '') && !isValidContractCups(cups)) errors.cups = 'property.estateSupplies.validation.cups'
  if (requireAddress && !String(form.address || '').trim()) errors.address = 'property.communityForm.validation.required'
  if (requirePostalCode && !String(form.postal_code || '').trim()) {
    errors.postal_code = 'property.communityForm.validation.required'
  } else if (form.postal_code && !/^\d{4,5}$/.test(String(form.postal_code).trim())) {
    errors.postal_code = 'property.estateForm.validation.postalCode'
  }
  if (form.type === 'light') {
    for (const period of POWER_PERIODS) {
      const number = toNumber(form[period])
      if (Number.isNaN(number) || (number !== null && number < 0)) errors[period] = 'property.estateForm.validation.power'
    }
  }
  return errors
}

/** Payload para supply/create/{inmueble} o supply/update/{id}. */
export const estateSupplyPayload = (form) => {
  const clean = (text) => (String(text ?? '').trim() === '' ? null : String(text).trim())
  const payload = {
    type: form.type,
    cups: normalizeContractCups(form.cups) || null,
    address: clean(form.address),
    postal_code: clean(form.postal_code),
    rate: clean(form.rate),
  }
  for (const period of POWER_PERIODS) {
    payload[period] = form.type === 'light' ? toNumber(form[period]) : null
  }
  return payload
}
